import { useEffect, useMemo, useRef, useState, useTransition } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import { Input, Select } from "@/components/ui/input";
import { InterviewStatus, DECISIONS } from "@/lib/constants";
import { useDict } from "@/lib/i18n/context";

const STATUSES: InterviewStatus[] = [
  "invited",
  "in_progress",
  "completed",
  "violated",
  "expired",
];

export interface FilterState {
  q: string;
  status: string;
  decision: string;
  job: string;
  from: string;
  to: string;
}

export function FilterBar({
  basePath = "/interviews",
  jobs = [],
  initial,
}: {
  basePath?: string;
  jobs?: { id: string; title: string }[];
  initial: FilterState;
}) {
  const t = useDict();
  const navigate = useNavigate();
  const [pending, startTransition] = useTransition();
  const [state, setState] = useState<FilterState>(initial);
  const first = useRef(true);

  const isDirty = useMemo(
    () =>
      Boolean(
        state.q || state.status || state.decision || state.job || state.from || state.to,
      ),
    [state],
  );

  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    const timer = setTimeout(() => {
      const params = new URLSearchParams();
      if (state.q.trim()) params.set("q", state.q.trim());
      if (state.status) params.set("status", state.status);
      if (state.decision) params.set("decision", state.decision);
      if (state.job) params.set("job", state.job);
      if (state.from) params.set("from", state.from);
      if (state.to) params.set("to", state.to);
      const qs = params.toString();
      startTransition(() =>
        navigate(qs ? `${basePath}?${qs}` : basePath, { replace: true }),
      );
    }, 300);
    return () => clearTimeout(timer);
  }, [state, basePath, navigate]);

  function set<K extends keyof FilterState>(key: K, value: FilterState[K]) {
    setState((s) => ({ ...s, [key]: value }));
  }

  function clearAll() {
    setState({ q: "", status: "", decision: "", job: "", from: "", to: "" });
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
          <Input
            value={state.q}
            onChange={(e) => set("q", e.target.value)}
            placeholder={t.filters.searchPlaceholder}
            className="ps-9"
          />
        </div>
        {jobs.length > 0 && (
          <Select
            value={state.job}
            onChange={(e) => set("job", e.target.value)}
            className="sm:w-48"
          >
            <option value="">{t.filters.allJobs}</option>
            <option value="none">{t.filters.generalOnly}</option>
            {jobs.map((j) => (
              <option key={j.id} value={j.id}>
                {j.title}
              </option>
            ))}
          </Select>
        )}
        <Select
          value={state.status}
          onChange={(e) => set("status", e.target.value)}
          className="sm:w-40"
        >
          <option value="">{t.filters.allStatuses}</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {t.status[s]}
            </option>
          ))}
        </Select>
        <Select
          value={state.decision}
          onChange={(e) => set("decision", e.target.value)}
          className="sm:w-40"
        >
          <option value="">{t.filters.allDecisions}</option>
          {DECISIONS.map((d) => (
            <option key={d} value={d}>
              {t.decision[d]}
            </option>
          ))}
          <option value="none">{t.decision.none}</option>
        </Select>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm text-muted">
          {t.filters.from}
          <Input
            type="date"
            value={state.from}
            onChange={(e) => set("from", e.target.value)}
            className="h-9 w-auto"
          />
        </label>
        <label className="flex items-center gap-2 text-sm text-muted">
          {t.filters.to}
          <Input
            type="date"
            value={state.to}
            onChange={(e) => set("to", e.target.value)}
            className="h-9 w-auto"
          />
        </label>
        {isDirty && (
          <button
            type="button"
            onClick={clearAll}
            className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-sm text-muted hover:text-foreground"
          >
            <X className="h-4 w-4" /> {t.filters.clear}
          </button>
        )}
        <span className={pending ? "text-xs text-muted" : "sr-only"} aria-live="polite">
          …
        </span>
      </div>
    </div>
  );
}
